<script lang="ts">
  import AUButton from "component_library/components/svelte/ui/AUButton.svelte"
  import AUStack from "component_library/components/svelte/ui/AUStack.svelte"
  import { leadFlowIcon } from "conversion_flows/types/leadFlowIcons"
  import { ComponentProps, getContext } from "svelte"
  import { getFlowsContext } from "website_script/contexts/flows"
  import Svg from "website_script/components/Svg.svelte"
  import ConversionBoxDealerInfo from "website_script/components/ConversionBoxDealerInfo.svelte"
  import ConversionBoxInsights from "website_script/components/ConversionBoxInsights.svelte"
  import IntegrationTypes from "website_script/constants/IntegrationTypes"
  import { getBorderRadiusFromCornerStyle } from "website_script/lib/buttonHelpers"
  import { ElementData } from "website_script/lib/Parsers"
  import { Environment } from "website_script/types/environment"
  import { Settings } from "website_script/types/settings"
  import { ButtonConfig } from "website_script/types/widgets"
  import { CUSTOM_CSS_NAMES } from "website_script/lib/customCss"

  export let elementData: ElementData

  const { trackFunction } = getContext<Environment>("environment")
  const { triggerFlow } = getFlowsContext()
  const {
    conversionBoxButtonsConfig,
    conversionBoxButtonCornerStyle,
    conversionBoxButtonShowIcon,
    conversionBoxShowDealerInfo,
    conversionBoxShowInsights,
    conversionBoxHorizontalButtons,
    car,
  } = getContext<Settings>("settings")
  const { buttons } = conversionBoxButtonsConfig

  const buttonsStyle = `--au-button-border-radius: ${getBorderRadiusFromCornerStyle(
    conversionBoxButtonCornerStyle
  )};`

  trackFunction({
    entity: "conversion-box",
    action: "displayed",
    flow: buttons.map((b) => b.flow_type),
    type: "displayed_cta",
  })

  const _triggerFlow = (buttonConfig: ButtonConfig) => {
    triggerFlow(
      buttonConfig,
      IntegrationTypes.CONVERSION_BOX,
      undefined,
      elementData
    )
    trackFunction({
      entity: "conversion-box",
      element: "flow_button",
      action: "clicked",
      flow: buttonConfig.flow_type,
      type: "interacted_with_cta",
    })
  }

  const stackProps: ComponentProps<AUStack> = conversionBoxHorizontalButtons
    ? {
        layout: "horizontal",
        wrap: "wrap",
      }
    : {}

  const showBox =
    (conversionBoxShowInsights && car) ||
    buttons.length > 0 ||
    conversionBoxShowDealerInfo
</script>

<!--
@component
Renders The Box.
-->
{#if showBox}
  <div id={CUSTOM_CSS_NAMES.CONVERSION_BOX_CONTAINER} class="au-conversion-box">
    <AUStack>
      {#if conversionBoxShowInsights && car}
        <ConversionBoxInsights {car} />
      {/if}

      {#if buttons.length > 0}
        <AUStack
          id={CUSTOM_CSS_NAMES.CTA_BUTTONS_CONTAINER}
          gap="xs"
          alignItems="center"
          justifyContent="center"
          {...stackProps}
          style={buttonsStyle}
        >
          {#each buttons as buttonConfig}
            {#if conversionBoxButtonShowIcon}
              <!--
                We repeat AUButton because the slot won't work as expected inside the condition.
                See https://github.com/sveltejs/svelte/issues/5604
              -->
              <AUButton
                fullWidth
                color={buttonConfig.primary ? "primary" : "secondary"}
                size="lg"
                onClick={() => _triggerFlow(buttonConfig)}
                className={CUSTOM_CSS_NAMES.CTA_BUTTON}
              >
                <Svg
                  alt={buttonConfig.text}
                  aria-hidden="true"
                  data={leadFlowIcon(buttonConfig.icon)}
                  slot="leftIcon"
                />

                {buttonConfig.text}
              </AUButton>
            {:else}
              <AUButton
                fullWidth
                color={buttonConfig.primary ? "primary" : "secondary"}
                size="lg"
                onClick={() => _triggerFlow(buttonConfig)}
                className={CUSTOM_CSS_NAMES.CTA_BUTTON}
              >
                {buttonConfig.text}
              </AUButton>
            {/if}
          {/each}
        </AUStack>
      {/if}

      {#if conversionBoxShowDealerInfo}
        <ConversionBoxDealerInfo {elementData} />
      {/if}
    </AUStack>
  </div>
{/if}

<style>
  .au-conversion-box {
    /* TODO: move this to a custom CSS solution (that dealers have access to) */
    margin: 10px 0;
    /* Make sure floating ancestors don't break the layout */
    clear: both;
    /* Define container for usage with container queries */
    container-type: inline-size;
  }

  .au-conversion-box :global([data-layout="horizontal"] > .au-button) {
    max-width: 320px;
  }
</style>
